import React, { useState } from "react";
import {
  Button,
  Modal,
  Alert,
  Space,
  notification
} from "antd";
import {
  SendOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useCustomMutation } from "@refinedev/core";
import { SUBMIT_SOURCING_MUTATION } from "../queries";
import { SourcingStatus, Sourcing } from "../interface";
import WorkflowDiagram from "../../purchase-template/components/WorkflowDiagram";

interface SubmitButtonProps {
  sourcingId: string;
  sourcing?: Sourcing;
  currentStatus: SourcingStatus;
  onSuccess?: () => void;
  disabled?: boolean;
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  sourcingId,
  sourcing,
  currentStatus,
  onSuccess,
  disabled = false,
}) => {
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);

  const { mutate: submitSourcing, isLoading: submitting } = useCustomMutation();

  // Handle form submission
  const handleSubmit = () => {
    setIsConfirmationModalVisible(true);
  };

  // Handle confirmation modal OK
  const handleConfirmationOk = async () => {
    setIsConfirmationModalVisible(false);
    try {
      submitSourcing(
        {
          url: "",
          method: "post",
          values: {
            input: { sourcingId },
          },
          meta: { gqlMutation: SUBMIT_SOURCING_MUTATION },
        },
        {
          onSuccess: () => {
            notification.success({
              message: 'Sourcing Submitted Successfully',
              description: 'The sourcing has been submitted for approval.',
            });
            onSuccess?.();
          },
          onError: (error: any) => {
            notification.error({
              message: 'Submission Failed',
              description: error.message || 'Failed to submit the sourcing.',
            });
          },
        },
      );
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  // Handle confirmation modal cancel
  const handleConfirmationCancel = () => {
    setIsConfirmationModalVisible(false);
  };

  const isDisabled = disabled || currentStatus !== SourcingStatus.DRAFT || submitting;

  return (
    <>
      {/* Submit Button */}
      <Button
        type="primary"
        icon={<SendOutlined />}
        onClick={handleSubmit}
        disabled={isDisabled}
        loading={submitting}
      >
        Submit for Approval
      </Button>

      {/* Confirmation Modal */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            Confirm Submission
          </Space>
        }
        open={isConfirmationModalVisible}
        onOk={handleConfirmationOk}
        onCancel={handleConfirmationCancel}
        okText="Submit"
        cancelText="Cancel"
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="No Conflicts Found"
            description="Your sourcing can be submitted successfully."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {/* Flow Diagram */}
        {sourcing?.approvalType === 'EXTERNAL' && sourcing?.externalWorkflow && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
            <WorkflowDiagram flow={sourcing.externalWorkflow} />
          </div>
        )}

        {sourcing?.approvalType === 'LOCAL' && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
            <Alert
              message="Local Approval Process"
              description="This sourcing will follow the standard local approval workflow."
              type="info"
              showIcon
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            By clicking "Submit", you confirm that:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>
              The first approver will be notified immediately
            </li>
            <li style={{ marginBottom: 4 }}>
              The sourcing will be marked as submitted
            </li>
            <li style={{ marginBottom: 0 }}>
              You will no longer be able to edit this sourcing
            </li>
          </ul>
        </div>
      </Modal>
    </>
  );
};